import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { parseENSource } from './utils/enSourceParser'
import { extractComments } from './utils/commentExtractor'
import { buildStatementNodeMaps } from './utils/statementNodeMap'
import type { DiagramScene, AnalysisResult } from './api/types'
import type { ParsedStatement, AnnotatedStatement } from './utils/types'

export type TabMode = 'source' | 'graph' | 'narrative'

const DEFAULT_SOURCE = `# comments start with hash

actor1
do: action1
needs: input
yields: shared
at: context1

actor2
do: action2
needs: shared
yields: output
at: context1

actor1, actor2
do: action3
needs: output
yields: result
at: context2`

function getInitialSource(): string {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (code) return code
    return DEFAULT_SOURCE
}

const initialSource = getInitialSource()
const initialParsed = parseENSource(initialSource)
const initialAnnotations = extractComments(initialSource, initialParsed.length)

interface PlaygroundState {
    // Source
    source: string
    projectName: string
    setSource: (source: string) => void
    setProjectName: (name: string) => void

    // Derived from source (recomputed on setSource)
    parsedStatements: ParsedStatement[]
    annotations: AnnotatedStatement[]

    // Layout
    leftOpen: boolean
    rightOpen: boolean
    leftWidth: number
    rightWidth: number
    activeTab: TabMode
    isMobile: boolean
    toggleLeft: () => void
    toggleRight: () => void
    setLeftOpen: (open: boolean) => void
    setRightOpen: (open: boolean) => void
    setLeftWidth: (width: number) => void
    setRightWidth: (width: number) => void
    setActiveTab: (tab: TabMode) => void
    setIsMobile: (isMobile: boolean) => void

    // Analysis results (synced from useENParser hook)
    result: AnalysisResult | null
    scene: DiagramScene | null
    isProcessing: boolean
    error: string | null
    nodeToStmt: Map<string, number>
    stmtToNode: Map<number, string>
    availableGroupings: string[]
    setAnalysis: (result: AnalysisResult | null, error: string | null) => void
    setProcessing: (isProcessing: boolean) => void

    // Selection
    selectedIds: Set<string>
    focusedNodeId: string | null
    selectedStatementIndex: number | null
    selectNode: (id: string, shiftKey?: boolean) => void
    clearSelection: () => void
    selectAll: () => void
    dismissEntity: () => void
    handleSelectStatement: (index: number | null) => void
    pendingFocusNodeId: string | null
    pendingFocusSeq: number
    requestFocusNode: (nodeId: string) => void
    clearPendingFocus: () => void

    // Canvas
    transform: { x: number; y: number; scale: number }
    setTransform: (t: { x: number; y: number; scale: number } | ((prev: { x: number; y: number; scale: number }) => { x: number; y: number; scale: number })) => void

    // View
    groupBy: string | undefined
    setGroupBy: (g: string | undefined) => void
    direction: string | undefined  // 'LR' | 'TB' | undefined (auto)
    setDirection: (d: string | undefined) => void

    // Navigation
    focusStack: string[]
    scrollPositions: Map<string, number>
    pushFocus: (id: string) => void
    popFocus: () => void
    resetFocus: () => void
    setScrollPosition: (key: string, position: number) => void
    getScrollPosition: (key: string) => number

    // Examples
    loadExample: (source: string, name: string) => void
}

export const usePlayground = create<PlaygroundState>()(
    persist(
        (set, get) => ({
            // ── Source ──
            source: initialSource,
            projectName: '',
            parsedStatements: initialParsed,
            annotations: initialAnnotations,

            setSource: (source) => {
                const parsedStatements = parseENSource(source)
                const annotations = extractComments(source, parsedStatements.length)
                set({ source, parsedStatements, annotations })
            },

            setProjectName: (projectName) => set({ projectName }),

            // ── Layout ──
            leftOpen: true,
            rightOpen: true,
            leftWidth: 360,
            rightWidth: 400,
            activeTab: 'graph' as TabMode,
            isMobile: false,

            toggleLeft: () => set((s) => ({ leftOpen: !s.leftOpen })),
            toggleRight: () => set((s) => ({ rightOpen: !s.rightOpen })),
            setLeftOpen: (leftOpen) => set({ leftOpen }),
            setRightOpen: (rightOpen) => set({ rightOpen }),
            setLeftWidth: (leftWidth) => set({ leftWidth }),
            setRightWidth: (rightWidth) => set({ rightWidth }),
            setActiveTab: (activeTab) => set({ activeTab }),
            setIsMobile: (isMobile) => set({ isMobile }),

            // ── Analysis ──
            result: null,
            scene: null,
            isProcessing: false,
            error: null,
            nodeToStmt: new Map(),
            stmtToNode: new Map(),
            availableGroupings: [],

            setAnalysis: (result, error) => {
                let scene: DiagramScene | null = null
                if (result?.sceneJson) {
                    try { scene = JSON.parse(result.sceneJson) } catch { scene = null }
                }

                const { parsedStatements } = get()
                const { nodeToStmt, stmtToNode } = buildStatementNodeMaps(parsedStatements, scene)

                // Compute available groupings
                const groups: string[] = []
                if (result && result.actionCount > 0 && scene) {
                    const actors = new Set(scene.nodes?.filter((n) => n.actorLabel).map((n) => n.actorLabel))
                    const hasLocations = (scene.locationGroups?.length ?? 0) > 0
                    if (actors.size >= 2) groups.push('actor')
                    if (hasLocations) groups.push('location')
                }

                set({ result, error, scene, nodeToStmt, stmtToNode, availableGroupings: groups })
            },

            setProcessing: (isProcessing) => set({ isProcessing }),

            // ── Selection ──
            selectedIds: new Set<string>(),
            focusedNodeId: null,
            selectedStatementIndex: null,

            selectNode: (id, shiftKey = false) => {
                const state = get()
                if (!id) {
                    set({
                        selectedIds: new Set(),
                        focusedNodeId: null,
                        selectedStatementIndex: null,
                    })
                    return
                }
                if (shiftKey) {
                    const next = new Set(state.selectedIds)
                    if (next.has(id)) next.delete(id)
                    else next.add(id)
                    set({ selectedIds: next })
                } else {
                    const newFocused = state.focusedNodeId === id ? null : id
                    const updates: Partial<PlaygroundState> = {
                        selectedIds: new Set([id]),
                        focusedNodeId: newFocused,
                        selectedStatementIndex: state.nodeToStmt.get(id) ?? null,
                    }
                    if (!state.isMobile && !state.rightOpen) {
                        updates.rightOpen = true
                    }
                    set(updates)
                }
            },

            clearSelection: () => set({
                selectedIds: new Set(),
                focusedNodeId: null,
                selectedStatementIndex: null,
            }),

            selectAll: () => {
                const { scene } = get()
                if (scene) {
                    set({ selectedIds: new Set(scene.nodes.map((n) => n.id)) })
                }
            },

            dismissEntity: () => set({
                focusedNodeId: null,
                selectedStatementIndex: null,
                selectedIds: new Set(),
            }),

            handleSelectStatement: (index) => {
                if (index === null) {
                    set({
                        selectedStatementIndex: null,
                        focusedNodeId: null,
                        selectedIds: new Set(),
                    })
                } else {
                    set({ selectedStatementIndex: index })
                }
            },

            pendingFocusNodeId: null,
            pendingFocusSeq: 0,
            requestFocusNode: (nodeId) => set((s) => ({ pendingFocusNodeId: nodeId, pendingFocusSeq: s.pendingFocusSeq + 1 })),
            clearPendingFocus: () => set({ pendingFocusNodeId: null }),

            // ── Canvas ──
            transform: { x: 0, y: 0, scale: 1 },
            setTransform: (t) => {
                if (typeof t === 'function') set((s) => ({ transform: t(s.transform) }))
                else set({ transform: t })
            },

            // ── View ──
            groupBy: undefined,
            setGroupBy: (groupBy) => set({ groupBy }),
            direction: undefined,
            setDirection: (direction) => set({ direction }),

            // ── Navigation ──
            focusStack: [],
            scrollPositions: new Map(),
            pushFocus: (id) => set((s) => ({ focusStack: [...s.focusStack, id] })),
            popFocus: () => set((s) => ({ focusStack: s.focusStack.slice(0, -1) })),
            resetFocus: () => set({ focusStack: [], scrollPositions: new Map() }),
            setScrollPosition: (key, position) => {
                const map = new Map(get().scrollPositions)
                map.set(key, position)
                set({ scrollPositions: map })
            },
            getScrollPosition: (key) => get().scrollPositions.get(key) ?? 0,

            // ── Examples ──
            loadExample: (source, name) => {
                const parsedStatements = parseENSource(source)
                const annotations = extractComments(source, parsedStatements.length)
                set({
                    source,
                    projectName: name,
                    parsedStatements,
                    annotations,
                    focusedNodeId: null,
                    selectedStatementIndex: null,
                    selectedIds: new Set(),
                })
            },
        }),
        {
            name: 'en-playground',
            partialize: (state) => ({
                source: state.source,
                projectName: state.projectName,
                groupBy: state.groupBy,
            }),
            merge: (persisted, current) => {
                const merged = { ...current, ...(persisted as object) }
                // URL ?code= param always takes priority over localStorage
                const params = new URLSearchParams(window.location.search)
                const code = params.get('code')
                if (code) (merged as any).source = code
                // Recompute derived state from restored source
                const source = (merged as any).source as string
                const parsedStatements = parseENSource(source)
                const annotations = extractComments(source, parsedStatements.length)
                return { ...merged, parsedStatements, annotations } as PlaygroundState
            },
        },
    ),
)
